import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Divider,
  Card,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Tooltip,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import { QRCodeCanvas } from "qrcode.react";

import { apiGetForwarderDepositAddress } from "./deposits";
import { CHAIN_META, TOKENS_BY_CHAIN, toDepositTokenSymbol } from "./tokens";
import usdtBscIcon from "../../../assets/images/usdt-bsc.svg";
import usdcBscIcon from "../../../assets/images/usdc-bsc.svg";




const GLASS = {
  bg: "linear-gradient(135deg, rgba(18,22,28,.96) 0%, rgba(10,13,17,.92) 100%)",
  border: "1px solid rgba(255,255,255,.10)",
  text: "#e5e7eb",
  subtext: "#9ca3af",
};

function cacheKeyForDeposit(chainId, token) {
  return `deposit_addr:${chainId}:${String(token).toUpperCase()}`;
}

function readDepositCache(chainId, token) {
  try {
    const k = cacheKeyForDeposit(chainId, token);
    const raw = localStorage.getItem(k);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj?.address) return null;

    const ts = Number(obj.ts || 0);
    const TTL = 7 * 24 * 60 * 60 * 1000;
    if (ts && Date.now() - ts > TTL) return null;

    return obj;
  } catch {
    return null;
  }
}

function writeDepositCache(chainId, token, payload) {
  try {
    const k = cacheKeyForDeposit(chainId, token);
    localStorage.setItem(k, JSON.stringify({ ...payload, ts: Date.now() }));
  } catch {
    // ignore
  }
}

export default function TransferCryptoModal({
  open,
  onClose,
  defaultToken = "USDT",
}) {
  // фиксируем сеть: только BSC
  const chainId = 56;
  const chainName = CHAIN_META?.[chainId]?.name || "BNB Chain";

  const [tokenSymbol, setTokenSymbol] = useState(defaultToken);
    const tokenIcon = tokenSymbol === "USDC" ? usdcBscIcon : usdtBscIcon;
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  const [copiedAddr, setCopiedAddr] = useState(false);

  const tokensForChain = useMemo(() => {
    const arr = TOKENS_BY_CHAIN?.[chainId] || [];
    return arr
      .map((t) => ({ symbol: t.symbol, isNative: Boolean(t.isNative) }))
      .filter((t) => t.symbol === "USDT" || t.symbol === "USDC");
  }, []);

  useEffect(() => {
    if (!open) return;
    const exists = tokensForChain.some((t) => t.symbol === tokenSymbol);
    if (!exists) setTokenSymbol(tokensForChain[0]?.symbol || "USDT");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    async function load() {
      const tokenForDeposit = toDepositTokenSymbol(chainId, tokenSymbol);

      const cached = readDepositCache(chainId, tokenForDeposit);
      if (cached?.address && !cancelled) {
        setData(cached);
        setErr("");
        setLoading(false);
      } else {
        setLoading(true);
        setErr("");
        setData(null);
      }

      try {
        const resp = await apiGetForwarderDepositAddress({ chainId, token: tokenForDeposit });
        if (cancelled) return;

        setData(resp);
        setErr("");
        setLoading(false);

        if (resp?.address && resp?.status !== "deploying") {
          writeDepositCache(chainId, tokenForDeposit, resp);
        }
      } catch (e) {
        const d = e?.response?.data;

        if (e?.response?.status === 202 && d?.status === "deploying") {
          if (cancelled) return;

          const hasReady = Boolean(cached?.address && cached?.status !== "deploying");
          if (!hasReady) setData(d);

          setErr("");
          setLoading(false);
          return;
        }

        const msg =
          d?.userMessage ||
          (typeof d?.error === "string" ? d.error : null) ||
          (typeof d === "string" ? d : null) ||
          "Sorry, please try again in 1 minute.";

        if (!cancelled) {
          setErr(String(msg));
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [open, tokenSymbol]);

  async function copyDepositAddress() {
    const addr = data?.address;
    if (!addr) return;
    try {
      await navigator.clipboard.writeText(String(addr));
      setCopiedAddr(true);
      setTimeout(() => setCopiedAddr(false), 1200);
    } catch {
      // ignore
    }
  }

  const isDeploying = data?.status === "deploying";
  const address = String(data?.address || "");
  const qrValue = address || " ";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: GLASS.bg,
          border: GLASS.border,
          color: GLASS.text,
          boxShadow: "0 24px 70px rgba(0,0,0,.55)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.2, pt: 1.8, pb: 1.2, display: "flex", alignItems: "center", gap: 1.2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 950, fontSize: 18, lineHeight: 1.2 }}>
            Deposit
          </Typography>
          <Typography sx={{ color: GLASS.subtext, fontSize: 12, mt: 0.3 }}>
            Select token and send only via the correct network.
          </Typography>
        </Box>

        <IconButton onClick={onClose} sx={{ ml: "auto", color: "#cbd5e1" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: "rgba(148,163,184,.16)" }} />

      <DialogContent sx={{ px: 2.2, py: 1.8 }}>
        {/* Top line: Network + Token */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
          <Box
  component="img"
  src={tokenIcon}
  alt={`${tokenSymbol} on BSC`}
  sx={{
    width: 56,
    height: 56,
    flex: "0 0 auto",
    borderRadius: 3,
    boxShadow: "0 10px 28px rgba(0,0,0,.45)",
  }}
/>

          <Chip
            label={`${chainName} (BSC)`}
            sx={{
              background: "rgba(2,6,23,.55)",
              border: "1px solid rgba(255,255,255,.10)",
              color: "#E5E7EB",
              fontWeight: 900,
            }}
          />

          <FormControl size="small" sx={{ minWidth: 220, flex: "1 1 220px" }}>
            <InputLabel sx={{ color: "rgba(229,231,235,.7)" }}>Token</InputLabel>
            <Select
              value={tokenSymbol}
              label="Token"
              onChange={(e) => setTokenSymbol(String(e.target.value))}
              sx={{
                color: GLASS.text,
                background: "rgba(2,6,23,.55)",
                borderRadius: 2,
                ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,.10)" },
              }}
            >
              {tokensForChain.map((t) => (
                <MenuItem key={t.symbol} value={t.symbol}>
                  {t.symbol}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Content */}
        <Card
          elevation={0}
          sx={{
            mt: 1.6,
            borderRadius: 2,
            border: "1px solid rgba(148,163,184,.18)",
            background: "rgba(15,23,42,.55)",
            p: 1.6,
          }}
        >
          {loading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <CircularProgress size={18} />
              <Typography sx={{ color: GLASS.subtext, fontSize: 13 }}>
                Loading deposit address…
              </Typography>
            </Box>
          )}

          {!loading && err && (
            <Typography sx={{ color: "#ff6b6b", fontSize: 13 }}>
              {err}
            </Typography>
          )}

          {!loading && !err && isDeploying && (
            <>
              <Typography sx={{ fontSize: 12, color: GLASS.subtext }}>
                Deposit address is being prepared
              </Typography>

              <Box
                sx={{
                  mt: 0.8,
                  p: 1.2,
                  borderRadius: 2,
                  background: "rgba(2,6,23,.55)",
                  border: "1px solid rgba(255,255,255,.10)",
                }}
              >
                <Typography sx={{ color: "rgba(229,231,235,.85)", fontSize: 13, lineHeight: 1.45 }}>
                  {data?.userMessage || "Deposit address is being prepared. Please try again in 1 minute."}
                </Typography>

                {data?.retryAfterSec ? (
                  <Typography sx={{ mt: 0.6, color: GLASS.subtext, fontSize: 12 }}>
                    Retry in ~{data.retryAfterSec}s.
                  </Typography>
                ) : null}
              </Box>

              <Box sx={{ mt: 1.2, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  onClick={() => setTokenSymbol((t) => t)}
                  variant="outlined"
                  sx={{
                    textTransform: "none",
                    borderColor: "rgba(148,163,184,.6)",
                    color: "#cbd5e1",
                  }}
                >
                  Try again
                </Button>
              </Box>
            </>
          )}

          {!loading && !err && data && !isDeploying && (
            <>
              {/* Address (top, prominent) */}
              <Typography sx={{ fontSize: 12, color: GLASS.subtext }}>
                Deposit address
              </Typography>

              <Box
                sx={{
                  mt: 0.8,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1.2,
                  borderRadius: 2,
                  background: "rgba(2,6,23,.55)",
                  border: "1px solid rgba(255,255,255,.10)",
                }}
              >
                <Typography
                  component="code"
                  sx={{
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                    fontSize: 13,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                    color: "#E5E7EB",
                  }}
                  title={address}
                >
                  {address}
                </Typography>

                <Tooltip title={copiedAddr ? "Copied" : "Copy"}>
                  <IconButton onClick={copyDepositAddress} sx={{ color: "#cbd5e1" }} size="small">
                    {copiedAddr ? (
                      <CheckCircleOutlineIcon fontSize="inherit" />
                    ) : (
                      <ContentCopyIcon fontSize="inherit" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Big QR (below, center) */}
              <Box
                sx={{
                  mt: 2.0,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    p: 1.2,
                    borderRadius: 3,
                    background: "#ffffff",
                    lineHeight: 0,
                  }}
                >
                  <QRCodeCanvas value={qrValue} size={260} includeMargin />
                </Box>
              </Box>

              <Typography sx={{ mt: 1.4, color: GLASS.subtext, fontSize: 12, textAlign: "center" }}>
                Send only {tokenSymbol} via {chainName}. Wrong network may result in loss.
              </Typography>

              <Box sx={{ mt: 1.6, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  onClick={onClose}
                  variant="outlined"
                  sx={{
                    textTransform: "none",
                    borderColor: "rgba(148,163,184,.6)",
                    color: "#cbd5e1",
                  }}
                >
                  Done
                </Button>
              </Box>
            </>
          )}
        </Card>
      </DialogContent>
    </Dialog>
  );
}
